import { useState, useRef } from "react";
import { Card } from "primereact/card";
import { FileUpload } from "primereact/fileupload";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { getPresignedUrl, uploadFileToS3 } from "../../api/fileService";
import { fetchAuthSession } from "aws-amplify/auth"; // Import fetchAuthSession
import { ProgressBar } from "primereact/progressbar";
import config from "../../config"; // Import config

export default function Upload() {
  const fileUploadRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async (event) => {
    const file = event.files[0]; // Only handle the first file

    if (!file) {
      setIsUploading(false);
      setMessage({
        severity: "error",
        text: "No file selected. Please choose a .xlsm file to upload.",
      });
      return;
    }

    // Clear the message when the upload starts
    setMessage(null);
    setIsUploading(true);

    // Enforce .xlsm extension regardless of how the file is selected
    if (!file.name.endsWith(".xlsm")) {
      setMessage({
        severity: "error",
        text: `Only .xlsm files are allowed.`,
      });
      setIsUploading(false);
      fileUploadRef.current.clear();
      return;
    }

    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken; // Get the ID token

      if (!token) {
        throw new Error("User token is missing. Please log in.");
      }
      const { presignedUrl, key, encodedFileName } = await getPresignedUrl(
        file.name,
        file.type,
        token
      );

      const renamedFileName = key.split("/").pop();

      const decodedFileName = decodeURIComponent(encodedFileName);
      console.log("Decoded File Name:", decodedFileName);

      // Upload the file and track progress
      await uploadFileToS3(
        presignedUrl,
        file,
        (progress) => {
          setProgress(progress); // Update the progress state
        },
        { originalfilename: encodedFileName } // used encoded filename as metadata
      );

      setMessage({
        severity: "success",
        text: `File uploaded successfully and renamed to "${renamedFileName}".`,
      });
      setProgress(0); // Reset progress after successful upload
      fileUploadRef.current.clear(); // Clear the file list after successful upload
    } catch (error) {
      console.error(
        `Error during custom upload for file "${file.name}":`,
        error
      );
      setMessage({
        severity: "error",
        text: `Error uploading "${file.name}". Please try again.`,
      });
      setProgress(0); // Reset progress on error
      fileUploadRef.current.clear(); // Clear the file list on error
    } finally {
      setIsUploading(false); // Reset uploading state
    }
  };

  return (
    <Card
      title="Upload"
      style={{ border: "none", boxShadow: "none", padding: "0.75rem" }}
      data-testid="upload-container"
    >
      <div style={{ marginBottom: "1rem" }}>
        <p>
          <strong>Maximum File Size:</strong>{" "}
          {config.FEATURE_FLAGS.DATA_UPLOAD.maxFileSize / (1024 * 1024)}MB
        </p>
        <p>
          <strong>Allowed File Types:</strong> .xlsm
        </p>
      </div>
      {message && <Message severity={message.severity} text={message.text} />}
      <FileUpload
        aria-label="file-upload"
        ref={fileUploadRef}
        id="file-upload"
        data-testid="upload-input"
        pt={{
          chooseButton: { "data-testid": "choose-button" },
        }}
        auto
        accept=".xlsm"
        customUpload
        maxFileSize={config.FEATURE_FLAGS.DATA_UPLOAD.maxFileSize} // Use value from config
        invalidFileSizeMessageSummary="Invalid file size"
        invalidFileSizeMessageDetail={`Maximum upload size is ${
          config.FEATURE_FLAGS.DATA_UPLOAD.maxFileSize / (1024 * 1024)
        }MB.`}
        chooseLabel="Browse"
        disabled={isUploading}
        emptyTemplate={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Button
              style={{
                borderRadius: "50%",
                height: "2.357rem",
                width: "2.357rem",
              }}
              rounded
              aria-label="upload-button-empty-upload-area"
              icon="pi pi-upload"
              onClick={() => {
                const button = document.querySelector(
                  ".p-button.p-fileupload-choose.p-component"
                );
                button?.click();
              }}
              disabled={isUploading}
            />
            <span
              style={{
                marginTop: "10px",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              Drag and drop file or Choose file to upload.
            </span>
          </div>
        }
        progressBarTemplate={<ProgressBar value={progress} showValue={false} />}
        uploadHandler={handleUpload}
        onValidationFail={(file) =>
          console.log("Validation failed for file:", file.name)
        }
        onClear={() => {
          // setProgress(0); // Reset progress when cleared
          // setMessage(null); // Clear message when cleared
          console.log("File upload cleared.");
        }}
        onError={(error) => {
          console.error("Upload error:", error);
        }}
        onRemove={(file) => {
          console.log("File removed:", file.name);
        }}
        onSelect={(event) => {
          console.log("File selected logging from inside onSelect function:", event.files[0].name);
        }}
        itemTemplate={(file, options) => {
          return (
            <div
              data-testid="file-item-template"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                padding: "0.5rem 0",
                marginBottom: "0.5rem",
              }}
            >
              {/* File details */}
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "0.9rem",
                  color: "#333",
                  marginBottom: "0.25rem",
                }}
              >
                {file.name}
              </div>
              <div style={{ fontSize: "0.8rem", color: "#666" }}>
                {options.formatSize}
              </div>
            </div>
          );
        }}
      />
    </Card>
  );
}
